import { Fragment, useMemo, useState } from 'react'
import { EventRow } from './EventRow'
import { EventSheet } from './EventSheet'
import { Button, Empty, Skeleton } from '../ui'
import { useFeed, useShowings, useVenues } from '../lib/queries'
import { useProfile } from '../lib/profile'
import { addDays, fmt, iso } from '../lib/calendar'
import { berlinDay } from '../lib/rowMeta'
import { languageAllowed, laneOf, talkLanguageAllowed } from '../lib/entries'
import { programmeFor, type HallVenue } from '../lib/place'
import type { FeedRow } from '../lib/types'
import { Failed } from '../screens/Stub'

// 40 rows of day headings and all pushed the Konzerthaus's "All" segment to
// 5,050px against the brief's 5,000px budget — 30 clears it with room to
// spare. Not a virtualiser (3.2 has not landed one this package can reuse).
const STEP = 30

/** How far ahead each segment reaches, in days from today. `null` is no
 *  ceiling at all — "All" is bounded only by the feed's own horizon
 *  (`HORIZON_WEEKS`, `queries.ts`), which every screen already lives inside. */
const SCOPE_DAYS: Record<'week' | 'month' | 'all', number | null> = {
  week: 7,
  month: 31,
  all: null,
}

/**
 * A building's own programme: what plays here, chronologically, under a
 * heading per day — `Places`' row for P1.
 *
 * Rule 3 of the brief: nothing is fetched for this venue specifically. The
 * three shared queries every screen already holds are windowed and grouped
 * here, the same way `Feed.tsx` windows the same feed into its own lanes —
 * `programmeFor` (`lib/place.ts`) is the one piece of that worth testing off
 * a screenshot: which building a showing actually happened in, and which
 * hall.
 */
export function PlaceProgramme({ venueId, scope }: { venueId: string; scope: 'week' | 'month' | 'all' }) {
  const feedQ = useFeed()
  const showQ = useShowings()
  const venuesQ = useVenues()
  const { english_only: englishOnly, talks_language: talksLanguage } = useProfile()

  const [open, setOpen] = useState<FeedRow | null>(null)
  const [shown, setShown] = useState(STEP)

  const today = iso(new Date())

  const placed = useMemo(() => {
    if (!feedQ.data || !showQ.data || !venuesQ.data) return []
    const venues = new Map<string, HallVenue>(
      venuesQ.data.list.map(v => [v.id, { id: v.id, name: v.name, parent_id: v.parent_id }]))
    // The same rules every list in the app filters by: nothing hidden by
    // taste, nothing already over, nothing Doug can't follow — `Places`' own
    // prototype ran on the first three; `talkLanguageAllowed` (WP 6.3) joined
    // once Browse and Today started applying it, so a venue's own programme
    // cannot show a German-only tour or talk that Browse already hid.
    const rows = feedQ.data.filter(e =>
      languageAllowed(e, englishOnly) && talkLanguageAllowed(e, talksLanguage) && !laneOf(e).hidden && e.type !== 'news'
      && (e.run_end ?? e.run_start ?? today) >= today)
    return programmeFor(venueId, rows, showQ.data.rows, venues)
  }, [feedQ.data, showQ.data, venuesQ.data, venueId, today, englishOnly, talksLanguage])

  const windowed = useMemo(() => {
    const days = SCOPE_DAYS[scope]
    if (days == null) return placed
    const cutoff = addDays(today, days)
    return placed.filter(({ row }) => (row.run_start ?? row.published_on ?? today) <= cutoff)
  }, [placed, scope, today])

  const sorted = useMemo(() => [...windowed].sort((a, b) => {
    const da = a.row.run_start ?? a.row.published_on ?? '9999-12-31'
    const db = b.row.run_start ?? b.row.published_on ?? '9999-12-31'
    return da < db ? -1 : da > db ? 1 : 0
  }), [windowed])

  const pending = feedQ.isPending || showQ.isPending || venuesQ.isPending
  const error = feedQ.error ?? showQ.error ?? venuesQ.error
  if (pending) return <Skeleton variant="row" count={4} />
  if (error) return <Failed error={error.message} />

  const byEntry = showQ.data!.byEntry
  const visible = sorted.slice(0, shown)

  if (!visible.length) {
    return <Empty title={scope === 'all' ? 'Nothing upcoming here.' : 'Nothing in this window.'} />
  }

  let lastDay = ''
  return (
    <>
      {visible.map(({ row, hall }) => {
        const day = berlinDay(row.run_start ?? row.published_on ?? today)
        const heading = day !== lastDay
        lastDay = day
        // `showHall` reads `e.venue` raw (`rowMeta.ts`) — substituted with the
        // hall's own name only where a showing actually named one; a row
        // that plays the building itself keeps whatever it already had.
        const display: FeedRow = hall ? { ...row, venue: hall } : row
        return (
          <Fragment key={row.id}>
            {heading && (
              <p className="placeday">{fmt(day, { weekday: 'long', day: 'numeric', month: 'long' })}</p>
            )}
            <EventRow e={display} showings={byEntry.get(row.id)} showHall onOpen={setOpen} />
          </Fragment>
        )
      })}

      {sorted.length > shown && (
        <div className="placemore">
          <Button variant="secondary" size="sm" onClick={() => setShown(n => n + STEP)}>
            Load {Math.min(STEP, sorted.length - shown)} more
          </Button>
        </div>
      )}

      <EventSheet e={open} onClose={() => setOpen(null)} />
    </>
  )
}
